import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { useWindows } from "../WindowManager";
import {
  DOCUMENTS_DIR,
  findByPathSegment,
  getNode,
  getTrail,
  pathOf,
} from "../fs";
import "./apps.css";

const BANNER = [
  "Windows PowerShell",
  "Copyright (C) Microsoft Corporation. All rights reserved.",
  "",
  "Install the latest PowerShell for new features and improvements!",
  "",
  "Type `help` for the commands this shell knows.",
  "",
];

const HELP = [
  "  dir, ls          list the contents of the current directory",
  "  cd <name|..>     change directory (cd \\ goes to This PC)",
  "  type, cat <file> print a text file",
  "  tree             show the directory tree from here",
  "  start <name>     open a file or app the way Explorer would",
  "  pwd              print the working directory",
  "  whoami           current user",
  "  ver              Windows version",
  "  ipconfig         network configuration",
  "  systeminfo       machine summary",
  "  date, time       current date and time",
  "  echo <text>      print text",
  "  theme            toggle light / dark mode",
  "  cls, clear       clear the screen",
];

const pad = (s, n) => String(s).padEnd(n);

export default function TerminalApp() {
  const { isDark, toggleTheme } = useTheme();
  const { open, apps } = useWindows();
  const [cwd, setCwd] = useState(DOCUMENTS_DIR.id);
  const [lines, setLines] = useState(() => BANNER.map((text) => ({ text })));
  const [value, setValue] = useState("");
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [lines]);

  const print = (entries) => setLines((prev) => [...prev, ...entries]);
  const node = getNode(cwd);
  const prompt = `PS ${pathOf(cwd) || "This PC"}>`;

  const listing = (dirNode) => {
    const children = dirNode.children || [];
    if (!children.length) return [{ text: "" }, { text: "  (empty)" }, { text: "" }];
    return [
      { text: "" },
      { text: `    Directory: ${pathOf(dirNode.id) || "This PC"}` },
      { text: "" },
      { text: "Mode    LastWriteTime        Length Name", dim: true },
      { text: "----    -------------        ------ ----", dim: true },
      ...children.map((c) => ({
        text: `${c.type === "file" ? "-a---" : "d----"}   ${pad(
          c.modified || "",
          20
        )} ${pad(c.size || "", 6)} ${c.name}`,
        accent: c.type !== "file",
      })),
      { text: "" },
    ];
  };

  const tree = (dirNode, depth = 0, out = []) => {
    (dirNode.children || []).forEach((child, i, arr) => {
      const last = i === arr.length - 1;
      out.push({
        text: `${"    ".repeat(depth)}${last ? "└── " : "├── "}${child.name}`,
        accent: child.type !== "file",
      });
      if (child.children?.length && depth < 2) tree(child, depth + 1, out);
    });
    return out;
  };

  const run = (raw) => {
    const input = raw.trim();
    print([{ text: input, prompt }]);
    if (!input) return;
    setHistory((h) => [input, ...h]);
    setHistoryIdx(-1);

    const [cmd, ...args] = input.split(/\s+/);
    const arg = args.join(" ");

    switch (cmd.toLowerCase()) {
      case "help":
      case "get-help":
        print([{ text: "" }, ...HELP.map((text) => ({ text })), { text: "" }]);
        break;

      case "dir":
      case "ls":
      case "get-childitem":
        print(listing(node));
        break;

      case "cd":
      case "set-location": {
        if (!arg || arg === "\\" || arg === "/") {
          setCwd("this-pc");
          break;
        }
        if (arg === "..") {
          const trail = getTrail(cwd);
          setCwd(trail.length ? trail[trail.length - 1].id : "this-pc");
          break;
        }
        const target = findByPathSegment(node, arg);
        if (!target || target.type === "file") {
          print([
            { text: `cd : Cannot find path '${arg}' because it does not exist.`, error: true },
            { text: "" },
          ]);
          break;
        }
        setCwd(target.target || target.id);
        break;
      }

      case "type":
      case "cat":
      case "get-content": {
        const target = findByPathSegment(node, arg);
        if (!target || target.type !== "file") {
          print([
            { text: `type : Cannot find path '${arg}'.`, error: true },
            { text: "" },
          ]);
          break;
        }
        if (!target.text) {
          print([
            { text: `type : '${arg}' is not a text file. Try: start ${arg}`, error: true },
            { text: "" },
          ]);
          break;
        }
        print([
          { text: "" },
          ...target.text.split("\n").map((text) => ({ text })),
          { text: "" },
        ]);
        break;
      }

      case "tree":
        print([
          { text: "" },
          { text: pathOf(cwd) || "This PC", accent: true },
          ...tree(node),
          { text: "" },
        ]);
        break;

      case "start": {
        const target = findByPathSegment(node, arg);
        if (target) {
          if (target.type === "file" && target.open?.app) {
            open(target.open.app, { fileId: target.id, ...target.open });
            print([{ text: `Opening ${target.name}…` }, { text: "" }]);
          } else {
            open("explorer", { folderId: target.target || target.id });
            print([{ text: `Opening ${target.name} in File Explorer…` }, { text: "" }]);
          }
          break;
        }
        const app = apps.find((a) => a.id === arg.toLowerCase());
        if (app) {
          open(app.id);
          print([{ text: `Starting ${app.title}…` }, { text: "" }]);
          break;
        }
        print([
          {
            text: `start : Cannot find '${arg}'. Apps: ${apps
              .map((a) => a.id)
              .join(", ")}`,
            error: true,
          },
          { text: "" },
        ]);
        break;
      }

      case "pwd":
        print([{ text: pathOf(cwd) || "This PC" }, { text: "" }]);
        break;

      case "whoami":
        print([{ text: "earlpappi-pc\\earlpappi" }, { text: "" }]);
        break;

      case "ver":
        print([
          { text: "" },
          { text: "Microsoft Windows [Version 10.0.26100.1742]" },
          { text: "" },
        ]);
        break;

      case "ipconfig":
        print([
          { text: "" },
          { text: "Windows IP Configuration" },
          { text: "" },
          { text: "Wireless LAN adapter Wi-Fi:" },
          { text: "" },
          { text: "   Connection-specific DNS Suffix  . : localdomain" },
          { text: "   IPv4 Address. . . . . . . . . . . : 192.168.1.42" },
          { text: "   Subnet Mask . . . . . . . . . . . : 255.255.255.0" },
          { text: "   Default Gateway . . . . . . . . . : 192.168.1.1" },
          { text: "" },
        ]);
        break;

      case "systeminfo":
        print([
          { text: "" },
          { text: "Host Name:                 EARLPAPPI-PC" },
          { text: "OS Name:                   Microsoft Windows 11 Pro (replica)" },
          { text: "OS Version:                10.0.26100 N/A Build 26100" },
          { text: "System Type:               x64-based PC" },
          { text: "Total Physical Memory:     16,384 MB" },
          { text: "Registered Owner:          Miracle Aponjolosun" },
          { text: "" },
        ]);
        break;

      case "date":
      case "time":
        print([{ text: new Date().toString() }, { text: "" }]);
        break;

      case "echo":
      case "write-output":
        print([{ text: arg }, { text: "" }]);
        break;

      case "theme":
        toggleTheme();
        print([
          { text: `Switched to ${isDark ? "light" : "dark"} mode.` },
          { text: "" },
        ]);
        break;

      case "cls":
      case "clear":
        setLines([]);
        break;

      default:
        print([
          {
            text: `${cmd} : The term '${cmd}' is not recognized as a name of a cmdlet, function, script file, or executable program.`,
            error: true,
          },
          { text: "Check the spelling of the name, or type `help` for a list." },
          { text: "" },
        ]);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      run(value);
      setValue("");
      return;
    }
    if (e.key === "ArrowUp" && history.length) {
      e.preventDefault();
      const next = Math.min(historyIdx + 1, history.length - 1);
      setHistoryIdx(next);
      setValue(history[next]);
      return;
    }
    if (e.key === "ArrowDown" && historyIdx >= 0) {
      e.preventDefault();
      const next = historyIdx - 1;
      setHistoryIdx(next);
      setValue(next < 0 ? "" : history[next]);
    }
  };

  return (
    <div
      className={`terminal ${isDark ? "" : "terminal--light"}`}
      onClick={() => inputRef.current?.focus()}
    >
      {lines.map((line, i) => (
        <div
          key={i}
          className={`terminal__line ${line.error ? "terminal__err" : ""} ${
            line.accent ? "terminal__accent" : ""
          } ${line.dim ? "terminal__dim" : ""}`}
        >
          {line.prompt && (
            <span className="terminal__path">{line.prompt} </span>
          )}
          {line.text}
        </div>
      ))}

      <div className="terminal__input-row">
        <span className="terminal__path">{prompt}</span>
        <input
          ref={inputRef}
          className="terminal__input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Terminal input"
          autoComplete="off"
          spellCheck="false"
        />
      </div>
      <div ref={endRef} />
    </div>
  );
}
